import React, { useState, useRef, useEffect } from "react";
import { Doughnut, Line, Bar } from "react-chartjs-2";
import article from "./Content";
import WaldoAd from '../AdComponent';
import { 
  Chart, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
} from "chart.js";

Chart.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
); 

const StockBreakevenCalculator = () => {
  const content = article.find((item) => item.id === 5);
  const [activeTab, setActiveTab] = useState("calculator");
  const [isMobile, setIsMobile] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [analysisType, setAnalysisType] = useState("single"); // single, multiple, options

  const [inputs, setInputs] = useState({
    // Single trade inputs
    entryPrice: "50.00",
    shares: "100",
    commission: "10.00",
    fees: "5.00",
    
    // Multiple trades inputs
    totalInvestment: "5000",
    totalShares: "100",
    totalCommission: "15.00",
    
    // Options inputs
    optionPremium: "2.50",
    optionContracts: "1",
    strikePrice: "55.00",
    contractType: "call", // call or put
    
    // Common inputs
    currency: "1",
    targetPrice: "",
    riskFreeRate: "2.5"
  });

  const [trades, setTrades] = useState([
    { shares: "50", price: "48.00", commission: "5.00" },
    { shares: "50", price: "52.00", commission: "5.00" }
  ]);

  const [result, setResult] = useState(null);
  const [calculationHistory, setCalculationHistory] = useState([]);

  // Check mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-calculate when inputs change
  useEffect(() => {
    if (activeTab === "calculator") {
      calculateBreakeven();
    }
  }, [inputs, trades, analysisType, activeTab]);

  const addNotification = (message, type = "info") => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, 5000);
  };

  const handleChange = (e) => {
    setInputs({ ...inputs, [e.target.name]: e.target.value });
  };

  const handleTradeChange = (index, field, value) => {
    const updatedTrades = trades.map((trade, i) => 
      i === index ? { ...trade, [field]: value } : trade
    );
    setTrades(updatedTrades);
  };

  const addTrade = () => {
    setTrades([...trades, { shares: "", price: "", commission: "0.00" }]);
  };

  const removeTrade = (index) => {
    if (trades.length > 1) {
      setTrades(trades.filter((_, i) => i !== index));
    }
  };

  const calculateBreakeven = () => {
    let breakevenPrice = 0;
    let totalCost = 0;
    let totalShares = 0;
    let totalCommission = 0;
    let currencyRate = parseFloat(inputs.currency) || 1;

    switch (analysisType) {
      case "single":
        const entryPrice = parseFloat(inputs.entryPrice) || 0;
        const shares = parseFloat(inputs.shares) || 0;
        const commission = parseFloat(inputs.commission) || 0;
        const fees = parseFloat(inputs.fees) || 0;

        totalShares = shares;
        totalCommission = commission + fees;
        totalCost = (entryPrice * shares) + totalCommission;
        breakevenPrice = totalCost / shares;
        break;

      case "multiple":
        // Calculate from individual trades
        trades.forEach(trade => {
          const tradeShares = parseFloat(trade.shares) || 0;
          const tradePrice = parseFloat(trade.price) || 0;
          const tradeCommission = parseFloat(trade.commission) || 0;
          
          totalShares += tradeShares;
          totalCost += (tradeShares * tradePrice) + tradeCommission;
          totalCommission += tradeCommission;
        });

        if (totalShares > 0) {
          breakevenPrice = totalCost / totalShares;
        }
        break;

      case "options":
        const optionPremium = parseFloat(inputs.optionPremium) || 0;
        const contracts = parseFloat(inputs.optionContracts) || 0;
        const strikePrice = parseFloat(inputs.strikePrice) || 0;
        const contractType = inputs.contractType;

        totalShares = contracts * 100; // Each contract = 100 shares
        totalCost = optionPremium * totalShares;
        
        if (contractType === "call") {
          breakevenPrice = strikePrice + optionPremium;
        } else {
          breakevenPrice = strikePrice - optionPremium;
        }
        break;
    }

    // Calculate additional metrics
    const currentPrice = parseFloat(inputs.entryPrice) || parseFloat(trades[0]?.price) || 0;
    const priceDifference = breakevenPrice - currentPrice;
    const percentToBreakeven = currentPrice > 0 ? (priceDifference / currentPrice) * 100 : 0;
    const requiredGain = priceDifference * totalShares;

    // Time value calculations
    const riskFreeRate = parseFloat(inputs.riskFreeRate) || 0;
    const dailyRate = riskFreeRate / 36500; // Convert to daily decimal
    const targetPrice = parseFloat(inputs.targetPrice) || 0;
    const daysToTarget = targetPrice > currentPrice ? 
      Math.log(targetPrice / currentPrice) / dailyRate : 0;

    const resultData = {
      breakevenPrice: breakevenPrice * currencyRate,
      totalCost: totalCost * currencyRate,
      totalShares,
      totalCommission: totalCommission * currencyRate,
      currentPrice: currentPrice * currencyRate,
      priceDifference: priceDifference * currencyRate,
      percentToBreakeven,
      requiredGain: requiredGain * currencyRate,
      analysisType,
      currencyRate,
      daysToTarget: isFinite(daysToTarget) ? daysToTarget : 0,
      isProfitable: currentPrice >= breakevenPrice
    };

    setResult(resultData);
  };

  const calculateScenarioAnalysis = () => {
    if (!result) return [];

    const scenarios = [
      { price: result.currentPrice * 0.8, label: "-20%" },
      { price: result.currentPrice * 0.9, label: "-10%" },
      { price: result.breakevenPrice, label: "Breakeven" },
      { price: result.currentPrice * 1.1, label: "+10%" },
      { price: result.currentPrice * 1.2, label: "+20%" },
      { price: result.currentPrice * 1.5, label: "+50%" }
    ];

    return scenarios.map(scenario => {
      const profitLoss = (scenario.price - result.breakevenPrice) * result.totalShares;
      const percentChange = ((scenario.price - result.currentPrice) / result.currentPrice) * 100;
      
      return {
        ...scenario,
        profitLoss,
        percentChange,
        isBreakeven: Math.abs(scenario.price - result.breakevenPrice) < 0.01
      };
    });
  };

  const calculateTimeAnalysis = () => {
    if (!result || !inputs.targetPrice) return [];

    const targetPrice = parseFloat(inputs.targetPrice);
    const currentPrice = result.currentPrice;
    const riskFreeRate = parseFloat(inputs.riskFreeRate) || 2.5;
    const dailyReturn = riskFreeRate / 36500;

    const timeData = [];
    const months = 12;

    for (let i = 0; i <= months; i++) {
      const futureValue = currentPrice * Math.pow(1 + dailyReturn * 30, i);
      const profitLoss = (futureValue - result.breakevenPrice) * result.totalShares;
      const isBreakeven = futureValue >= result.breakevenPrice;

      timeData.push({
        month: i,
        price: futureValue,
        profitLoss,
        isBreakeven
      });
    }

    return timeData;
  };

  const resetCalculator = () => {
    setTrades([
      { shares: "50", price: "48.00", commission: "5.00" },
      { shares: "50", price: "52.00", commission: "5.00" }
    ]);
    setInputs({
      entryPrice: "50.00",
      shares: "100",
      commission: "10.00",
      fees: "5.00",
      totalInvestment: "5000",
      totalShares: "100",
      totalCommission: "15.00",
      optionPremium: "2.50",
      optionContracts: "1",
      strikePrice: "55.00",
      contractType: "call",
      currency: "1",
      targetPrice: "",
      riskFreeRate: "2.5"
    });
    addNotification("Calculator reset to default values", "success");
  };

  const exportToCSV = () => {
    if (!result) return;
    
    const csvContent = [
      "Metric,Value",
      `Breakeven Price,${result.breakevenPrice.toFixed(2)}`,
      `Current Price,${result.currentPrice.toFixed(2)}`,
      `Price Difference,${result.priceDifference.toFixed(2)}`,
      `Percent to Breakeven,${result.percentToBreakeven.toFixed(2)}%`,
      `Total Shares,${result.totalShares}`,
      `Total Cost,${result.totalCost.toFixed(2)}`,
      `Total Commission,${result.totalCommission.toFixed(2)}`,
      `Required Gain,${result.requiredGain.toFixed(2)}`,
      `Analysis Type,${result.analysisType}`
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'breakeven-analysis.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    addNotification("Data exported to CSV successfully", "success");
  };

  const printResult = () => {
    window.print();
  };

  const saveToHistory = () => {
    if (!result) return;
    
    const newCalculation = {
      id: Date.now(),
      timestamp: new Date().toLocaleString(),
      inputs: { ...inputs },
      trades: [...trades],
      analysisType,
      ...result
    };
    
    setCalculationHistory(prev => [newCalculation, ...prev.slice(0, 9)]);
    addNotification("Calculation saved to history", "success");
  };

  const loadCalculation = (calc) => {
    setInputs(calc.inputs);
    setTrades(calc.trades || []);
    setAnalysisType(calc.analysisType || "single");
    setResult(calc.result);
    setShowSaved(false);
    addNotification("Calculation loaded successfully", "success");
  };

  const quickPreset = (preset) => {
    const presets = {
      conservative: { 
        analysisType: "single",
        inputs: { entryPrice: "100.00", shares: "50", commission: "10.00" }
      },
      moderate: { 
        analysisType: "multiple",
        trades: [
          { shares: "100", price: "45.00", commission: "5.00" },
          { shares: "100", price: "55.00", commission: "5.00" }
        ]
      },
      aggressive: { 
        analysisType: "options",
        inputs: { optionPremium: "5.00", optionContracts: "2", strikePrice: "60.00" }
      }
    };
    
    const presetData = presets[preset];
    setAnalysisType(presetData.analysisType);
    
    if (presetData.trades) {
      setTrades(presetData.trades);
    }
    
    if (presetData.inputs) {
      setInputs(prev => ({ ...prev, ...presetData.inputs }));
    }
    
    addNotification(`${preset} preset applied`, "info");
  };

  // Chart Data
  const costChartData = result ? {
    labels: ["Stock Cost", "Commission & Fees"],
    datasets: [{
      data: [result.totalCost - result.totalCommission, result.totalCommission],
      backgroundColor: ["#19692C", "#FF6B6B"],
      borderWidth: 2,
      hoverOffset: 15
    }],
  } : null;

  const scenarioData = calculateScenarioAnalysis();
  const scenarioChartData = scenarioData.length > 0 ? {
    labels: scenarioData.map(s => s.label),
    datasets: [{
      label: 'Profit/Loss',
      data: scenarioData.map(s => s.profitLoss),
      backgroundColor: scenarioData.map(s => 
        s.isBreakeven ? '#FFD93D' : s.profitLoss >= 0 ? '#28A745' : '#E53E3E'
      ),
      borderColor: scenarioData.map(s => 
        s.isBreakeven ? '#FFD93D' : s.profitLoss >= 0 ? '#28A745' : '#E53E3E'
      ),
      borderWidth: 2
    }]
  } : null;

  const timeData = calculateTimeAnalysis();
  const timeChartData = timeData.length > 0 ? {
    labels: timeData.map(d => `Month ${d.month}`),
    datasets: [
      {
        label: 'Stock Price',
        data: timeData.map(d => d.price),
        borderColor: "#19692C",
        backgroundColor: "rgba(25, 105, 44, 0.1)",
        fill: true,
        tension: 0.4
      },
      {
        label: 'Breakeven Price',
        data: timeData.map(() => result.breakevenPrice),
        borderColor: "#FF6B6B",
        borderDash: [5, 5],
        fill: false,
        tension: 0.4
      }
    ]
  } : null;

  return (
    <div className={'profit-calculator'}>
      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notif => (
          <div key={notif.id} className={`notification ${notif.type}`}>
            <i className={`fas fa-${notif.type === 'success' ? 'check' : 'info'}-circle`}></i>
            {notif.message}
          </div>
        ))}
      </div>

      <div className="calculator-header">
        <div className="header-top">
          <h1>Stock Breakeven Calculator</h1>
        </div>
        <p>Calculate breakeven points, analyze profit scenarios, and optimize your exit strategies</p>
      </div>
<WaldoAd type="horizontal" /><br/>
      {/* Saved Calculations Modal */}
      {showSaved && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Saved Calculations</h3>
              <button className="close-btn" onClick={() => setShowSaved(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="saved-list">
              {savedCalculations.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-bookmark fa-2x"></i>
                  <p>No saved calculations yet</p>
                </div>
              ) : (
                savedCalculations.map(calc => (
                  <div key={calc.id} className="saved-item" onClick={() => loadCalculation(calc)}>
                    <div className="saved-name">{calc.name}</div>
                    <div className="saved-details">
                      <span className={`profit ${calc.isProfitable ? 'positive' : 'negative'}`}>
                        ${calc.breakevenPrice?.toFixed(2)}
                      </span>
                      <span className="date">{calc.timestamp}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <div className="calculator-layout">
        {/* Left Side - Inputs */}
        <div className="left-panel">
          {/* Navigation Tabs */}
          <div className="tabs-container">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === "calculator" ? "active" : ""}`}
                onClick={() => setActiveTab("calculator")}
              >
                <i className="fas fa-calculator"></i>
                {isMobile ? 'Calculator' : 'Breakeven Calc'}
              </button>
              <button 
                className={`tab ${activeTab === "scenarios" ? "active" : ""}`}
                onClick={() => setActiveTab("scenarios")}
              >
                <i className="fas fa-chart-bar"></i>
                {isMobile ? 'Scenarios' : 'Scenario Analysis'}
              </button>
              <button 
                className={`tab ${activeTab === "history" ? "active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                <i className="fas fa-history"></i>
                {isMobile ? 'History' : 'History'}
              </button>
            </div>
          </div>

          {/* Quick Presets */}
          <div className="presets-section">
            <h4>Quick Presets:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => quickPreset('conservative')}>
                Stock Trade
              </button>
              <button className="preset-btn moderate" onClick={() => quickPreset('moderate')}>
                Multiple Trades
              </button>
              <button className="preset-btn aggressive" onClick={() => quickPreset('aggressive')}>
                Options
              </button>
            </div>
          </div>

          {/* Analysis Type Selection */}
          <div className="mode-selection">
            <div className="mode-buttons">
              <button 
                className={`mode-btn ${analysisType === "single" ? "active" : ""}`}
                onClick={() => setAnalysisType("single")}
              >
                <i className="fas fa-chart-line"></i>
                Single Trade
              </button>
              <button 
                className={`mode-btn ${analysisType === "multiple" ? "active" : ""}`}
                onClick={() => setAnalysisType("multiple")}
              >
                <i className="fas fa-layer-group"></i>
                Multiple Trades
              </button>
              <button 
                className={`mode-btn ${analysisType === "options" ? "active" : ""}`}
                onClick={() => setAnalysisType("options")}
              >
                <i className="fas fa-chess-board"></i>
                Options
              </button>
            </div>
          </div>

          {/* Breakeven Calculator Tab */}
          {activeTab === "calculator" && (
            <div className="calculator-content">
              <div className="input-section">
                <div className="input-group">
                  {/* Single Trade Inputs */}
                  {analysisType === "single" && (
                    <>
                      <h3>
                        <i className="fas fa-trade"></i>
                        Single Trade Details
                      </h3>
                      <div className="input-row">
                        <div className="input-field">
                          <label><i className="fa-solid fa-dollar-sign"></i> Entry Price</label>
                          <input 
                            type="number" 
                            name="entryPrice" 
                            value={inputs.entryPrice} 
                            onChange={handleChange}
                            placeholder="0.00"
                            step="0.01"
                          />
                        </div>
                        <div className="input-field">
                          <label><i className="fa-solid fa-layer-group"></i> Shares</label>
                          <input 
                            type="number" 
                            name="shares" 
                            value={inputs.shares} 
                            onChange={handleChange}
                            placeholder="0"
                          />
                        </div>
                        <div className="input-field">
                          <label><i className="fa-solid fa-coins"></i> Commission</label>
                          <input 
                            type="number" 
                            name="commission" 
                            value={inputs.commission} 
                            onChange={handleChange}
                            placeholder="0.00"
                            step="0.01"
                          />
                        </div>
                        <div className="input-field">
                          <label><i className="fa-solid fa-file-invoice-dollar"></i> Other Fees</label>
                          <input 
                            type="number" 
                            name="fees" 
                            value={inputs.fees} 
                            onChange={handleChange}
                            placeholder="0.00"
                            step="0.01"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Multiple Trades Inputs */}
                  {analysisType === "multiple" && (
                    <>
                      <h3>
                        <i className="fas fa-shopping-cart"></i>
                        Multiple Trades
                      </h3>
                      
                      {trades.map((trade, index) => (
                        <div key={index} className="purchase-group">
                          <div className="purchase-header">
                            <h4>Trade #{index + 1}</h4>
                            {trades.length > 1 && (
                              <button 
                                className="remove-purchase-btn"
                                onClick={() => removeTrade(index)}
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            )}
                          </div><br/>
                          <div className="input-row">
                            <div className="input-field">
                              <label><i className="fa-solid fa-layer-group"></i> Shares</label>
                              <input 
                                type="number" 
                                value={trade.shares}
                                onChange={(e) => handleTradeChange(index, 'shares', e.target.value)}
                                placeholder="0"
                                min="0"
                              />
                            </div>
                            <div className="input-field">
                              <label><i className="fa-solid fa-dollar-sign"></i> Price</label>
                              <input 
                                type="number" 
                                value={trade.price}
                                onChange={(e) => handleTradeChange(index, 'price', e.target.value)}
                                placeholder="0.00"
                                step="0.01"
                                min="0"
                              />
                            </div>
                            <div className="input-field">
                              <label><i className="fa-solid fa-coins"></i> Commission</label>
                              <input 
                                type="number" 
                                value={trade.commission}
                                onChange={(e) => handleTradeChange(index, 'commission', e.target.value)}
                                placeholder="0.00"
                                step="0.01"
                                min="0"
                              />
                            </div>
                          </div>
                        </div>
                      ))}

                      <button className="btn add-purchase-btn" onClick={addTrade}>
                        <i className="fas fa-plus"></i>
                        Add Another Trade
                      </button>
                    </>
                  )}

                  {/* Options Inputs */}
                  {analysisType === "options" && (
                    <>
                      <h3>
                        <i className="fas fa-chess-board"></i>
                        Options Contract
                      </h3>
                      <div className="input-row">
                        <div className="input-field">
                          <label><i className="fa-solid fa-dollar-sign"></i> Option Premium</label>
                          <input 
                            type="number" 
                            name="optionPremium" 
                            value={inputs.optionPremium} 
                            onChange={handleChange}
                            placeholder="0.00"
                            step="0.01"
                          />
                        </div>
                        <div className="input-field">
                          <label><i className="fa-solid fa-file-contract"></i> Contracts</label>
                          <input 
                            type="number" 
                            name="optionContracts" 
                            value={inputs.optionContracts} 
                            onChange={handleChange}
                            placeholder="1"
                          />
                        </div>
                        <div className="input-field">
                          <label><i className="fa-solid fa-bullseye"></i> Strike Price</label>
                          <input 
                            type="number" 
                            name="strikePrice" 
                            value={inputs.strikePrice} 
                            onChange={handleChange}
                            placeholder="0.00"
                            step="0.01"
                          />
                        </div>
                        <div className="input-field">
                          <label><i className="fa-solid fa-arrows-alt-h"></i> Contract Type</label>
                          <select name="contractType" value={inputs.contractType} onChange={handleChange}>
                            <option value="call">Call Option</option>
                            <option value="put">Put Option</option>
                          </select>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Common Settings */}
                  <h3>
                    <i className="fas fa-cog"></i>
                    Analysis Settings
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-globe"></i> Currency Rate</label>
                      <input 
                        type="number" 
                        name="currency" 
                        value={inputs.currency} 
                        onChange={handleChange}
                        placeholder="1.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-bullseye"></i> Target Price (Optional)</label>
                      <input 
                        type="number" 
                        name="targetPrice" 
                        value={inputs.targetPrice} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Risk-Free Rate (%)</label>
                      <input 
                        type="number" 
                        name="riskFreeRate" 
                        value={inputs.riskFreeRate} 
                        onChange={handleChange}
                        placeholder="2.5"
                        step="0.1"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button className="btn reset-btn" onClick={resetCalculator}>
                  <i className="fas fa-redo"></i>
                  Reset
                </button>
                <button className="btn save-btn" onClick={saveToHistory}>
                  <i className="fas fa-save"></i>
                  Save to History
                </button>
              </div>
            </div>
          )}
<WaldoAd type="square" />
          {/* Scenario Analysis Tab */}
          {activeTab === "scenarios" && result && (
            <div className="projection-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-chart-bar"></i>
                  Scenario Analysis
                </h3>
                <div className="scenario-list">
                  {scenarioData.map((scenario, index) => (
                    <div key={index} className="scenario-item">
                      <div className="scenario-header">
                        <span className="scenario-name">{scenario.label}</span>
                        <span className={`scenario-outcome ${scenario.profitLoss >= 0 ? 'positive' : 'negative'}`}>
                          ${scenario.profitLoss.toFixed(2)}
                        </span>
                      </div>
                      <div className="scenario-stats">
                        <span>Price: ${scenario.price.toFixed(2)}</span>
                        <span>Change: {scenario.percentChange.toFixed(1)}%</span>
                        {scenario.isBreakeven && <span className="breakeven-badge">BREAKEVEN</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <div className="history-content">
              <h3>
                <i className="fas fa-history"></i>
                Recent Calculations
              </h3>
              {calculationHistory.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox fa-3x"></i>
                  <p>No calculations yet. Start calculating to see your history here.</p>
                </div>
              ) : (
                <div className="history-list">
                  {calculationHistory.map(calc => (
                    <div key={calc.id} className="history-item">
                      <div className="history-header">
                        <span className="history-date">
                          <i className="fas fa-clock"></i>
                          {calc.timestamp}
                        </span>
                        <span className={`history-profit ${calc.isProfitable ? 'positive' : 'negative'}`}>
                          <i className={`fas ${calc.isProfitable ? 'fa-arrow-up' : 'fa-arrow-down'}`}></i>
                          ${calc.breakevenPrice?.toFixed(2)}
                        </span> 
                      </div>
                      <div className="history-details">
                        <span><i className="fas fa-layer-group"></i> Shares: {calc.totalShares}</span>
                        <span><i className="fas fa-money-bill-wave"></i> Cost: ${calc.totalCost?.toFixed(2)}</span>
                        <span><i className="fas fa-balance-scale"></i> Type: {calc.analysisType}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Side - Results */}
        {(activeTab === "calculator" || activeTab === "scenarios") && (
          <div className="right-panel">
            {activeTab === "calculator" && (
              <div className="results-section">
                <div className="results-header">
                  <h2>
                    <i className="fas fa-crosshairs"></i>
                    Breakeven Analysis
                  </h2>
                  <div className="result-actions">
                    <button className="btn export-btn" onClick={exportToCSV}>
                      <i className="fas fa-file-export"></i>
                      {isMobile ? 'CSV' : 'Export CSV'}
                    </button>
                    <button className="btn print-btn" onClick={printResult}>
                      <i className="fas fa-print"></i>
                      {isMobile ? 'Print' : 'Print'}
                    </button>
                  </div>
                </div>
<WaldoAd type="square" />
                {result ? (
                  <>
                    <div className="results-grid">
                      <div className={`result-card highlight ${result.isProfitable ? 'positive' : 'negative'}`}>
                        <div className={`result-value ${result.isProfitable ? 'positive' : 'negative'}`}>
                          ${result.breakevenPrice.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-crosshairs"></i>
                          Breakeven Price
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          ${result.currentPrice.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-chart-line"></i>
                          Current Price
                        </div>
                      </div>
                      <div className="result-card">
                        <div className={`result-value ${result.priceDifference >= 0 ? 'positive' : 'negative'}`}>
                          {result.percentToBreakeven.toFixed(2)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-percentage"></i>
                          To Breakeven
                        </div>
                      </div>
                      <div className="result-card">
                        <div className={`result-value ${result.requiredGain >= 0 ? 'positive' : 'negative'}`}>
                          ${result.requiredGain.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-bullseye"></i>
                          Required Gain
                        </div>
                      </div>
                    </div>

                    <div className="detailed-results">
                      <div className="detail-row">
                        <span><i className="fas fa-layer-group"></i> Total Shares:</span>
                        <span>{result.totalShares}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-money-bill-wave"></i> Total Investment:</span>
                        <span>${result.totalCost.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-coins"></i> Total Commission:</span>
                        <span>${result.totalCommission.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-exchange-alt"></i> Price Difference:</span>
                        <span>${result.priceDifference.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-chess-board"></i> Analysis Type:</span>
                        <span>{result.analysisType}</span>
                      </div>
                      {result.daysToTarget > 0 && (
                        <div className="detail-row">
                          <span><i className="fas fa-clock"></i> Days to Target:</span>
                          <span>{Math.ceil(result.daysToTarget)} days</span>
                        </div>
                      )}
                    </div>

                    {/* Status Indicator */}
                    <div className="risk-summary">
                      <h4>Current Status</h4>
                      <div className="risk-cards">
                        <div className="risk-card">
                          <div className={`risk-value ${result.isProfitable ? 'positive' : 'negative'}`}>
                            {result.isProfitable ? 'PROFITABLE' : 'NOT PROFITABLE'}
                          </div>
                          <div className="risk-label">Trade Status</div>
                        </div>
                        <div className="risk-card">
                          <div className="risk-value moderate">
                            {result.percentToBreakeven >= 0 ? 'ABOVE' : 'BELOW'}
                          </div>
                          <div className="risk-label">Breakeven Level</div>
                        </div>
                      </div>
                    </div>

                    {/* Cost Breakdown Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-pie"></i>
                        Investment Breakdown
                      </h3>
                      <Doughnut 
                        data={costChartData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              position: isMobile ? 'top' : 'bottom'
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  return `$${context.parsed.toFixed(2)}`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>

                    {/* Time Analysis Chart */}
                    {timeData.length > 0 && (
                      <div className="chart-container">
                        <h3>
                          <i className="fas fa-chart-line"></i>
                          Time-Based Analysis
                        </h3>
                        <Line 
                          data={timeChartData}
                          options={{
                            responsive: true,
                            maintainAspectRatio: true,
                            plugins: {
                              legend: {
                                display: true,
                                position: 'top'
                              }
                            },
                            scales: {
                              y: {
                                beginAtZero: false,
                                ticks: {
                                  callback: function(value) {
                                    return '$' + value.toFixed(2);
                                  }
                                }
                              }
                            }
                          }}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <div className="empty-results">
                    <i className="fas fa-calculator fa-3x"></i>
                    <p>Enter your trade details to see breakeven analysis</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "scenarios" && result && (
              <div className="projection-results">
                <h3>
                  <i className="fas fa-chart-bar"></i>
                  Scenario Analysis
                </h3>
                <div className="chart-container">
                  <Bar 
                    data={scenarioChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          display: false
                        },
                        tooltip: {
                          callbacks: {
                            label: function(context) {
                              return `Profit/Loss: $${context.parsed.y.toFixed(2)}`;
                            }
                          }
                        }
                      },
                      scales: {
                        y: {
                          ticks: {
                            callback: function(value) {
                              return '$' + value.toLocaleString();
                            }
                          }
                        }
                      }
                    }}
                  />
                </div>
                <div className="projection-summary">
                  <div className="summary-card">
                    <div className="summary-value">
                      ${result.breakevenPrice.toFixed(2)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-crosshairs"></i>
                      Breakeven Point
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      {scenarioData.filter(s => s.profitLoss > 0).length}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-thumbs-up"></i>
                      Profitable Scenarios
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      ${Math.max(...scenarioData.map(s => s.profitLoss)).toFixed(2)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-arrow-up"></i>
                      Max Profit
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      ${Math.min(...scenarioData.map(s => s.profitLoss)).toFixed(2)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-arrow-down"></i>
                      Max Loss
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <br/><WaldoAd type="horizontal" />
      {content && content.content}
    </div>
  );
};

export default StockBreakevenCalculator;